"use client";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  IconButton,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";

export const GlobalFormWarningDialog = (props) => {
  const {
    open,
    onClose,
    onConfirm,
    title,
    message,
    cancelText,
    confirmText,
    confirmColor = "error",
    showIcon = true,
  } = props;
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            boxShadow: (theme) => theme.shadows[10],
          },
        },
      }}
    >
      <IconButton
        aria-label={t("common.close")}
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <FontAwesomeIcon icon={faXmark} style={{ fontSize: "1.25rem" }} />
      </IconButton>

      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {showIcon && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: "50%",
                color: (theme) =>
                  confirmColor === "error"
                    ? theme.palette.error.main
                    : theme.palette.warning.main,
              }}
            >
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                style={{ fontSize: "1.25rem" }}
              />
            </Box>
          )}
          <Box sx={{ fontWeight: 600, fontSize: "1.25rem" }}>
            {title || t("common.unsavedChanges")}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <DialogContentText
          sx={{ color: "text.secondary", fontSize: "0.95rem" }}
        >
          {message || t("common.unsavedChangesDialog")}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            minWidth: 100,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          {cancelText || t("common.continueEditing")}
        </Button>
        <Button
          onClick={onConfirm}
          color={confirmColor}
          variant="outlined"
          autoFocus
          sx={{
            minWidth: 100,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          {confirmText || t("common.discardChanges")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

GlobalFormWarningDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  cancelText: PropTypes.string,
  confirmText: PropTypes.string,
  confirmColor: PropTypes.oneOf([
    "primary",
    "secondary",
    "error",
    "warning",
    "info",
    "success",
  ]),
  showIcon: PropTypes.bool,
};
